import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Radar, AlertTriangle, BookOpen, MessageSquare, CheckCircle,
  ArrowRight, Search, Tag, Clock, History
} from 'lucide-react';

export default function DocsExceptionRadar() {
  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
          Exception Radar
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Exception Radar
        </h1>
        <p className="text-lg text-[#94A3B8] mb-6">
          Capture exceptions, route approvals, and turn edge cases into precedent. 
          Every exception you record becomes searchable precedent — and reduces repeats.
        </p>
        <div className="flex gap-4">
          <Link to="/dashboard/exception-radar">
            <Button className="bg-amber-500 text-white hover:bg-amber-600" data-testid="go-to-exception-radar">
              Open Exception Radar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Why Exception Radar */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Why Exception Radar?
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6">
          <p className="text-[#94A3B8] mb-4">
            In real operations, edge cases aren't rare — they're the job:
          </p>
          <ul className="space-y-2 text-[#94A3B8]">
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Inventory conflicts
            </li>
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Ship-complete tradeoffs
            </li>
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Expedite decisions
            </li>
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Refund exceptions
            </li>
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              "Save this customer" overrides
            </li>
          </ul>
          <p className="text-[#94A3B8] mt-4">
            Most teams resolve these in Slack and the knowledge disappears. 
            <strong className="text-white"> Exception Radar</strong> turns those moments into durable assets.
          </p>
        </div>
      </section>

      {/* Key Features */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Key Features
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Radar className="w-6 h-6 text-amber-400 mb-2" />
              <CardTitle className="text-white">Log Exceptions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Capture your next cancellation override, expedite decision, or refund exception 
                with structured reason codes and notes.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <MessageSquare className="w-6 h-6 text-sky-400 mb-2" />
              <CardTitle className="text-white">Request Approvals</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Route exception approvals into Slack for fast approve/reject decisions. 
                Keep your team aligned without leaving their workflow.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <BookOpen className="w-6 h-6 text-emerald-400 mb-2" />
              <CardTitle className="text-white">Mark as Precedent</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Turn resolved exceptions into reusable precedent. 
                Future similar cases can cite and apply the same decision.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Search className="w-6 h-6 text-purple-400 mb-2" />
              <CardTitle className="text-white">Search & Apply</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Search your precedent library when facing a new edge case. 
                Apply existing precedent to handle it faster and more consistently.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Exception Lifecycle */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Exception Lifecycle
        </h2>
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-sky-400 font-bold">1</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Log Exception</h3>
              <p className="text-sm text-[#94A3B8]">
                Record the exception with type, severity, order reference, and rationale.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-amber-400 font-bold">2</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Request Approval (Optional)</h3>
              <p className="text-sm text-[#94A3B8]">
                Send to Slack for manager/team approval. Decision is captured automatically.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-emerald-400 font-bold">3</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Resolve & Close</h3>
              <p className="text-sm text-[#94A3B8]">
                Apply the decision and mark as resolved. Add outcome notes if needed.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-400 font-bold">4</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Mark as Precedent</h3>
              <p className="text-sm text-[#94A3B8]">
                If this is a reusable pattern, mark it as precedent for future cases.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Exception Types */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Common Exception Types
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { type: 'Cancellation Override', desc: 'Customer requests cancellation after cutoff' },
            { type: 'Expedite Request', desc: 'Urgent delivery needed beyond normal SLA' },
            { type: 'Refund Exception', desc: 'Full/partial refund outside standard policy' },
            { type: 'Inventory Hold', desc: 'Reserve inventory for specific customer/order' },
            { type: 'Price Adjustment', desc: 'Manual pricing override after checkout' },
            { type: 'Shipping Override', desc: 'Special routing or carrier exception' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
              <Tag className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-medium mb-1">{item.type}</h3>
                <p className="text-sm text-[#94A3B8]">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Available in Plans */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Plan Availability
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-[#1E293B] rounded-lg">
              <span className="text-white font-medium">Exception Radar (View + Basic Capture)</span>
              <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Starter</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#1E293B] rounded-lg">
              <span className="text-white font-medium">Slack Approvals + Precedent Library</span>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Pro</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          Next Steps
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/approval-queue">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-amber-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Approval Queue →</h3>
                <p className="text-sm text-[#94A3B8]">Manage pending approvals in bulk</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/slack-integration">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-amber-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Slack Integration →</h3>
                <p className="text-sm text-[#94A3B8]">Set up Slack approval workflows</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
